import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CopilotService } from 'src/app/copilot/services/copilot.service';
import { usageColor } from 'src/app/copilot/utils/usage-color.util';
import { AuthService } from 'src/app/auth.service';

interface ChartBar {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  date: string;
  percent: number;
  usedCredits: number;
  usedTokens: number;
  showLabel: boolean;
  labelX: number;
  labelText: string;
}

/**
 * Popup opened by clicking a usage bar in Settings > Users — a 30-day daily
 * usage chart for one Analyst. Plain SVG, no charting library (none exists
 * anywhere in this app) — bars colored via the same usageColor() util the
 * inline progress bar uses, so a given percentage always looks the same.
 */
@Component({
  selector: 'app-user-usage-history-dialog',
  templateUrl: './user-usage-history-dialog.component.html',
  styleUrls: ['./user-usage-history-dialog.component.scss'],
  standalone: false,
})
export class UserUsageHistoryDialogComponent implements OnInit {
  userName: string;
  loading = true;
  error = false;
  resetting = false;
  resetError = false;

  /** Set once a reset actually succeeds, so close() can tell the Users table
   * to refresh its bars — most turns through this dialog never reset anything,
   * so the table shouldn't reload every time the dialog merely opens/closes. */
  private _didReset = false;

  readonly chartWidth = 600;
  readonly chartHeight = 200;
  readonly barAreaHeight = 160; // leaves room below for date labels

  bars: ChartBar[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { userId: number | string; userName: string; orgId: string },
    private copilotSvc: CopilotService,
    private authService: AuthService,
    private dialogRef: MatDialogRef<UserUsageHistoryDialogComponent>,
  ) {
    this.userName = data.userName;
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  isSuperAdmin(): boolean {
    const user = this.authService.getUserDetails();
    return user !== null && user.userType === 1;
  }

  loadHistory(): void {
    this.loading = true;
    this.copilotSvc.getUsageHistory(this.data.orgId, this.data.userId, 30).subscribe({
      next: (points) => {
        this.buildChart(points);
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  onResetUsage(): void {
    if (!confirm(`Reset today's DTR Copilot usage for ${this.userName}? They will be able to use Copilot again immediately.`)) {
      return;
    }
    this.resetting = true;
    this.resetError = false;
    this.copilotSvc.resetUserUsageToday(this.data.orgId, this.data.userId, 1).subscribe({
      next: () => {
        this.resetting = false;
        this._didReset = true;
        this.loadHistory();
      },
      error: () => {
        this.resetting = false;
        this.resetError = true;
      },
    });
  }

  close(): void {
    this.dialogRef.close({ reset: this._didReset });
  }

  private buildChart(points: { date: string; usedCredits: number; limitCredits: number; usedTokens: number }[]): void {
    const n = points.length;
    if (n === 0) {
      this.bars = [];
      return;
    }
    const gap = 2;
    const barWidth = (this.chartWidth - gap * (n - 1)) / n;

    this.bars = points.map((p, i) => {
      const percent = p.limitCredits ? Math.min(100, (p.usedCredits / p.limitCredits) * 100) : 0;
      const height = (percent / 100) * this.barAreaHeight;
      const x = i * (barWidth + gap);
      const showLabel = i % 5 === 0 || i === n - 1;
      return {
        x,
        y: this.barAreaHeight - height,
        width: barWidth,
        height,
        color: usageColor(percent),
        date: p.date,
        percent: Math.round(percent * 10) / 10,
        usedCredits: p.usedCredits,
        usedTokens: p.usedTokens,
        showLabel,
        labelX: x + barWidth / 2,
        labelText: showLabel ? this.shortDate(p.date) : '',
      };
    });
  }

  private shortDate(iso: string): string {
    const parts = iso.split('-'); // "YYYY-MM-DD"
    return parts.length === 3 ? `${parts[1]}/${parts[2]}` : iso;
  }
}
